/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { ROUTES } from '@sportninja/common/constants/app'

import { teamForm } from '../../components/Form/form-configs'
import { Flex } from '../../components/Layout'
import Picture from '../../components/Picture'
import useGetListItems from '../../components/List/useGetListItems'
import OptionButtonEdit from '../../components/List/OptionButtons/Edit'
import OptionButtonDelete from '../../components/List/OptionButtons/Delete'
import OptionButtonFavourite from '../../components/List/OptionButtons/Favourite'
import List from '../../components/List'
import { FIRST_CELL_WIDTH } from '../../components/List/list-constants'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import EditEntitySubTitle from '../EditEntitySubTitle'
import MoreOptions from '../../components/List/OptionButtons/More'
import { get } from 'mobx'

export const TeamCell = ({ item }) => (
  <Flex alignItems='center'>
    <Picture
      size='kxsmall'
      square
      fit
      imageId={getImageThumbnailId(item)}
      iconName='user-friends'
      css={css`
        margin-right: 8px;
      `}
    />
    {item.name}
  </Flex>
)

const TeamList = ({
  setCount = () => {},
  parentId,
  slug,
  refreshKey,
  soccerSport,
  hockeySport,
}) => {
  const [filter, setFilter] = useState('')
  const listProps = useGetListItems(slug, {
    onComplete: (response) => setCount(response.meta.pagination.count),
  })
  const { sort, direction, getItems, permissions, debouncedSearchTerm } =
    listProps

  useEffect(() => {
    getItems()
  }, [refreshKey, sort, direction, debouncedSearchTerm])

  useEffect(() => {
    if (filter === '') {
      getItems()
    } else {
      getItems(1, { sport_id: filter })
    }
  }, [filter])

  const headers = [
    {
      text: t('common:name'),
      sizePercent: 50,
      sort: 'name_full',
      Cell: TeamCell,
      to: (item) => ({
        pathname: `${ROUTES.TEAM_ROOT}/${item.id}`,
        state: { fromParent: parentId },
      }),
    },
    // Removing the parent org as requested by Jeff
    // {
    //   text: t('common:parentOrg'),
    //   sizePercent: 50,
    //   minWidth: FIRST_CELL_WIDTH,
    //   sort: 'organization.name_full',
    //   accessor: 'organization.name_full',
    //   to: (item) => `${ROUTES.ORG_ROOT}/${item.organization.id}`,
    // },
    {
      text: 'Sport',
      sizePercent: 50,
      sort: 'sport_id',
      accessor: (item) =>
        item?.sport_id === soccerSport?.id
          ? soccerSport?.name_full
          : hockeySport?.name_full || '-',
    },
    // {
    //   text: t('common:player', { count: 2 }),
    //   sizePercent: 8,
    //   sort: 'players_count',
    //   accessor: 'players_count',
    // },
  ]

  const options = (item) => {
    const permission = permissions[item.id]

    const onEdit = async (...args) => {
      const [values] = args
      const { imageData, ...form } = values

      if (form.sport_type) {
        if (form.sport_type === 'Ice Hockey') {
          form.sport_id = hockeySport?.id
        }
        if (form.sport_type === 'Soccer') {
          form.sport_id = soccerSport?.id
        }
      } else {
        form.sport_id = hockeySport?.id
      }

      if (!form?.name) {
        form.name = form.name_full
      }

      if (form?.delete_image) {
        await req(`/teams/${item.id}/image`, {
          method: 'DELETE',
        })
      }

      const body = JSON.stringify(form)
      const response = await req(`/teams/${item.id}`, {
        method: 'PUT',
        body,
      })
      if (imageData) {
        await req(`/teams/${item.id}/image`, {
          method: 'POST',
          body: imageData,
        })
      }
      await getItems()
      return response
    }

    const opts = [
      permission.update && (
        <OptionButtonEdit
          key='edit'
          form={teamForm(
            parsers[ENTITY_TYPES.team]({ ...item, permission }),
            false,
            hockeySport?.id
          )}
          onSubmit={onEdit}
          title={t('Web:editTeam')}
          subTitle={
            <EditEntitySubTitle id={item.id} entityType={ENTITY_TYPES.team} />
          }
          Delete={
            permission.delete && (
              <EntityFormDelete
                entityType={ENTITY_TYPES.team}
                itemId={item.id}
                title={`Delete ${item.name_full}`}
                type={t('common:team')}
                onComplete={getItems}
              />
            )
          }
        />
      ),
      <OptionButtonFavourite
        key='fav'
        entityType={ENTITY_TYPES.team}
        item={item}
        isFavourite={item.is_favorite}
      />,
      <MoreOptions
        key='more'
        options={[
          permission.delete && {
            key: 'delete',
            Component: OptionButtonDelete,
            componentProps: {
              useForceDelete: true,
              unmountOnClose: true,
              entityType: ENTITY_TYPES.team,
              itemId: item.id,
              title: `Delete ${item.name_full}`,
              type: t('common:team'),
              iconName: 'trash',
              onComplete: getItems,
            },
          },
        ]}
      />,
    ]

    return opts.filter((o) => o !== undefined)
  }

  return (
    <List
      headers={headers.filter((o) => o !== undefined)}
      options={options}
      listProps={listProps}
      noItemsText={t('common:team', { count: 2 })}
      showSearch
      showSportFilter={true}
      sportFilterOptions={[
        { text: 'All', value: '' },
        { text: soccerSport?.name_full, value: soccerSport?.id },
        { text: hockeySport?.name_full, value: hockeySport?.id },
      ]}
      setSportFilter={setFilter}
      sportFilter={filter}
    />
  )
}

export default TeamList
